import { Flask } from '@phosphor-icons/react';
import { useListFlowRunItems } from '@reelplicate/api-client/flow-runs';
import type { FlowItemDto, FlowItemStatus } from '@reelplicate/api-client/model';
import { Badge, EmptyState, Skeleton, Stack } from '@reelplicate/ui';

import { humanize } from '../../catalogue-ui';
import { ItemMedia } from './item-media';
import { isOpen, RUN_POLL_MS, useRun } from './run-state';

const STATUS: Record<
  FlowItemStatus,
  { label: string; tone: 'success' | 'warning' | 'danger' | 'neutral' }
> = {
  ACTIVE: { label: 'Passed on', tone: 'success' },
  HELD: { label: 'Held', tone: 'warning' },
  REJECTED: { label: 'Rejected', tone: 'danger' },
  DROPPED: { label: 'Filtered out', tone: 'neutral' },
  REPLACED: { label: 'Rewritten', tone: 'neutral' },
};

/**
 * The inspector's Test tab: what this node made in the run on the canvas
 * (Figma "Preview - from test run · script 1").
 */
export function NodeItems({ nodeId }: { nodeId: string }) {
  const { workspaceId, run } = useRun();
  const { data: items } = useListFlowRunItems(
    workspaceId,
    run?.id ?? '',
    { nodeId },
    {
      query: {
        enabled: Boolean(run),
        throwOnError: false,
        refetchInterval: isOpen(run) ? RUN_POLL_MS : false,
      },
    },
  );

  if (!run) {
    return (
      <EmptyState
        icon={Flask}
        titleAs="h3"
        title="Nothing run yet"
        description="Press Test run to push one item through the flow and see what this node makes, here."
      />
    );
  }
  if (!items) return <Skeleton className="h-24" shape="rectangle" />;
  if (items.length === 0) {
    return (
      <p className="text-body-sm text-text-secondary">
        This node hasn’t made anything in the {run.mode === 'TEST' ? 'test run' : 'run'}{' '}
        yet.
      </p>
    );
  }
  return (
    <Stack gap={2}>
      <p className="text-caption text-text-secondary">
        From the {run.mode === 'TEST' ? 'test run' : 'last run'} · {items.length}{' '}
        {items.length === 1 ? 'item' : 'items'}
      </p>
      {items.map((item) => (
        <ItemCard key={item.id} item={item} />
      ))}
    </Stack>
  );
}

function ItemCard({ item }: { item: FlowItemDto }) {
  const status = STATUS[item.status];
  const payload = item.payload;
  const angle = typeof payload.angle === 'string' ? humanize(payload.angle) : null;
  const body = typeof payload.body === 'string' ? payload.body : null;
  return (
    <article className="bg-bg-subtle flex flex-col gap-1.5 rounded-md p-3">
      <div className="flex items-center gap-2">
        {angle && (
          <span className="text-overline text-text-secondary uppercase">{angle}</span>
        )}
        <Badge tone={status.tone} className="ml-auto">
          {status.label}
        </Badge>
      </div>
      <ItemMedia item={item} />
      <p className="text-body-sm-strong text-text-primary">{item.summary}</p>
      {body && <p className="text-caption text-text-secondary line-clamp-3">{body}</p>}
      {item.reason && (
        <p className="text-caption text-status-warning-fg">{item.reason}</p>
      )}
    </article>
  );
}
